import { Card } from "@/components/ui/card";
import { CUSTOMER_TYPES } from "@/lib/constants/customer-types";
import { calculateBookingTotals } from "@/lib/pricing/booking-calculations";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol } from "@/lib/utils/currency";

interface PriceSummaryCardProps {
  booking: any;
  bookingDetails: any;
}

export function PriceSummaryCard({
  booking,
  bookingDetails,
}: PriceSummaryCardProps) {
  const { t } = useTranslation();
  const currency = bookingDetails?.currency || "SEK";
  const isCorporateBooking =
    booking.customer_type_id === 2 ||
    booking.customer_type?.name === CUSTOMER_TYPES.CORPORATE;

  // Get the correct base price and VAT info
  let unitPrice: number;
  let vatRate: number;

  if (
    booking.base_amount_excl_vat !== undefined &&
    booking.base_amount_excl_vat !== null
  ) {
    // Use the stored per-person price directly from the booking
    unitPrice =
      booking.price_type_id === 2
        ? (booking.per_person_mode_price_excluding_vat || booking.base_amount_excl_vat / (booking.participant_count || 1))
        : booking.base_amount_excl_vat;
    vatRate = booking.vat_rate || 0;
  } else {
    unitPrice = bookingDetails?.pricing?.price || 0;
    vatRate = bookingDetails?.pricing?.vat_percentage || 25;
  }

  // Note: Price modifier is already applied to the booking prices in the database
  // We just need to reference the modifier for display purposes
  const modifier = booking.price_modifier || booking.price_modifiers;

  // Use centralized calculation with the unit price as-is (modifier already applied)
  const totals = calculateBookingTotals(booking, unitPrice, vatRate);

  const showPricing = totals.shows;
  const addOnsTotal = totals.addOns.baseAmount;
  const addOnsVat = totals.addOns.vatAmount;
  const shopItemsTotal = totals.shopItems.baseAmount;
  const shopItemsVat = totals.shopItems.vatAmount;

  // Use database values directly for subtotal and VAT
  const baseAmountFromDb = booking.base_amount_excl_vat || 0;
  const vatAmountFromDb = booking.vat_amount || 0;

  // Partner-commission discount snapshotted onto the booking at creation.
  // Applies to show fees only — never to add-ons or shop items (matches
  // what Fortnox/PowerOffice apply on the generated invoice).
  const partnerCommissionPct =
    booking?.partner_id && booking?.partner_commission_percentage != null
      ? Number(booking.partner_commission_percentage)
      : 0;
  const partnerFraction = partnerCommissionPct / 100;
  const partnerName = booking?.partner?.company_name as string | undefined;
  const showsDiscountExclVat = baseAmountFromDb * partnerFraction;
  const showsDiscountVat = vatAmountFromDb * partnerFraction;

  // Calculate totals including add-ons and shop items, net of any partner
  // discount on the show portion.
  const subtotal =
    baseAmountFromDb - showsDiscountExclVat + addOnsTotal + shopItemsTotal;
  const totalVatAmount =
    vatAmountFromDb - showsDiscountVat + addOnsVat + shopItemsVat;
  const totalPrice = subtotal + totalVatAmount;

  // Calculate actual VAT percentage for shop items display
  const shopItemsVatPercentage =
    shopItemsTotal > 0 ? Math.round((shopItemsVat / shopItemsTotal) * 100) : 0;

  return (
    <Card className="py-0">
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-3">Price Summary</h2>

        <div className="space-y-4">
          {/* Shows Summary */}
          <div>
            <h3 className="font-medium mb-2">Shows</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>
                  Experience (
                  {(() => {
                    // Check for Period 2 modification data
                    let period2Details: any = null;
                    try {
                      // Parse details if it's a JSON string
                      if (typeof booking.details === 'string') {
                        period2Details = JSON.parse(booking.details);
                      } else {
                        period2Details = booking.details;
                      }
                    } catch (e) {
                      // If parsing fails, treat as null
                      period2Details = null;
                    }

                    const invoicedParticipantCount = period2Details?.invoiced_participant_count;

                    // Use invoiced count if it exists and is higher (Period 2 modification)
                    if (invoicedParticipantCount && invoicedParticipantCount > booking.participant_count) {
                      return invoicedParticipantCount;
                    }
                    // Otherwise use minimum charge if applicable
                    else if (bookingDetails?.minChargeParticipants &&
                             bookingDetails.minChargeParticipants > 0 &&
                             bookingDetails.minChargeParticipants > booking.participant_count) {
                      return bookingDetails.minChargeParticipants;
                    }
                    // Otherwise use current participant count
                    return booking.participant_count || 1;
                  })()}{" "}
                  ×{" "}
                  {booking.price_type_id === 2
                    ? (unitPrice || 0).toLocaleString()
                    : "Fixed Price"}{" "}
                  {getCurrencySymbol(currency)}
                  {(() => {
                    // Parse Period 2 details
                    let period2Details: any = null;
                    try {
                      if (typeof booking.details === 'string') {
                        period2Details = JSON.parse(booking.details);
                      } else {
                        period2Details = booking.details;
                      }
                    } catch (e) {
                      period2Details = null;
                    }

                    const invoicedParticipantCount = period2Details?.invoiced_participant_count;

                    // Show asterisk if Period 2 modification or minimum charge applies
                    if ((invoicedParticipantCount && invoicedParticipantCount > booking.participant_count) ||
                        (bookingDetails?.minChargeParticipants &&
                         bookingDetails.minChargeParticipants > 0 &&
                         bookingDetails.minChargeParticipants > booking.participant_count)) {
                      return " *";
                    }
                    return "";
                  })()})
                </span>
                <span>
                  {(booking.base_amount_excl_vat || 0).toLocaleString()} {getCurrencySymbol(currency)}
                </span>
              </div>
              {modifier && (
                <div className="flex justify-between text-sm px-2 py-1.5 bg-blue-50 border border-blue-200 rounded -mx-1">
                  <span className="flex items-center gap-2 text-blue-700">
                    <span>
                      {modifier.modifier_type === "discount" ? "🎉" : "⚠️"}
                    </span>
                    {modifier.name} ({Math.abs(modifier.modifier_value)}%{" "}
                    {modifier.modifier_type === "discount"
                      ? "discount"
                      : "surcharge"}
                    ) - Already included in price
                  </span>
                </div>
              )}
              {partnerCommissionPct > 0 && (
                <div className="flex justify-between text-purple-700">
                  <span>
                    🤝 Partner discount ({partnerCommissionPct}%
                    {partnerName ? ` — ${partnerName}` : ""})
                  </span>
                  <span>
                    −{Math.round(showsDiscountExclVat).toLocaleString()}{" "}
                    {getCurrencySymbol(currency)}
                  </span>
                </div>
              )}
              {!isCorporateBooking && (
                <div className="flex justify-between text-gray-500">
                  <span>VAT ({vatRate}%)</span>
                  <span>
                    {Math.round(
                      (booking.vat_amount || 0) - showsDiscountVat,
                    ).toLocaleString()}{" "}
                    {getCurrencySymbol(currency)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Add-ons Summary */}
          {booking.booking_add_ons?.length > 0 && (
            <div className="pt-4 border-t">
              <h3 className="font-medium mb-2">Add-ons</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Add-ons Subtotal</span>
                  <span>
                    {addOnsTotal.toLocaleString()} {getCurrencySymbol(currency)}
                  </span>
                </div>
                {!isCorporateBooking && addOnsVat > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Add-On VAT</span>
                    <span>
                      {addOnsVat.toLocaleString()} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Shop Items Summary */}
          {booking.booking_shop_items?.length > 0 && (
            <div className="pt-4 border-t">
              <h3 className="font-medium mb-2">Shop Items</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Shop Items Subtotal</span>
                  <span>
                    {shopItemsTotal.toLocaleString()} {getCurrencySymbol(currency)}
                  </span>
                </div>
                {!isCorporateBooking && (
                  <div className="flex justify-between text-gray-500">
                    <span>VAT</span>
                    <span>
                      {shopItemsVat.toLocaleString()} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="pt-4 border-t">
            <div className="space-y-2">
              {isCorporateBooking ? (
                <>
                  {/* Corporate booking - No VAT */}
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>
                      {subtotal.toLocaleString()} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* Individual booking - VAT included */}
                  <div className="flex justify-between font-medium">
                    <span>Total (excl. VAT)</span>
                    <span>
                      {subtotal.toLocaleString()} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Total VAT</span>
                    <span>
                      {totalVatAmount.toLocaleString()} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                    <span>Total (incl. VAT)</span>
                    <span>
                      {totalPrice.toLocaleString()} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                </>
              )}

              {/* Manual Override by Bar Staff */}
              {booking.manual_override &&
                booking.payment_method !== "invoice" &&
                booking.manual_override_amount && (
                  <div className="flex justify-between text-orange-700 text-sm">
                    <span>Bar Staff Override</span>
                    <span>
                      {booking.manual_override_amount.toLocaleString()}{" "}
                      {getCurrencySymbol(currency)}
                    </span>
                  </div>
                )}

              {/* Minimum charge / Period 2 modification notice */}
              {(() => {
                // Parse Period 2 details
                let period2Details: any = null;
                try {
                  if (typeof booking.details === 'string') {
                    period2Details = JSON.parse(booking.details);
                  } else {
                    period2Details = booking.details;
                  }
                } catch (e) {
                  period2Details = null;
                }

                const invoicedParticipantCount = period2Details?.invoiced_participant_count;

                if (invoicedParticipantCount && invoicedParticipantCount > booking.participant_count) {
                  // Period 2 modification
                  return (
                    <div className="text-xs text-muted-foreground pt-2">
                      <p>
                        * Period 2 modification - charging for original {invoicedParticipantCount} participants •
                        Prices {isCorporateBooking ? "exclude" : "include"} VAT
                      </p>
                    </div>
                  );
                } else if (bookingDetails?.minChargeParticipants &&
                           bookingDetails.minChargeParticipants > 0 &&
                           bookingDetails.minChargeParticipants > booking.participant_count) {
                  // Minimum charge
                  return (
                    <div className="text-xs text-muted-foreground pt-2">
                      <p>
                        * Minimum {bookingDetails.minChargeParticipants} participants charged •
                        Prices {isCorporateBooking ? "exclude VAT" : "include VAT"}
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
